<script setup>
import { onMounted, ref, nextTick } from 'vue'
import AnimatedBackground from '../../../AnimatedBackground.vue'
import PositiveWaveLoop1 from '../../../assets/animations/energetic_frequencies/loops/positive_wave_loop_1.json'
import CharacterEnlarge from '../text_animations/CharacterEnlarge.vue'
import CharacterJumpFromTheRight from '../text_animations/CharacterJumpFromTheRight.vue'
import TextSlideUp from '../text_animations/TextSlideUp.vue'
import AnimatedSkewedEllipsMask from '../shared/AnimatedSkewedEllipsMask.vue'
import { frames } from '../../../utils/frames.js'
const gsapTimeline = useGsapTimeline()

const props = defineProps({
  superTitle: String,
  title: String,
  subtitle: String,
  body: String,
  image: String,

  colorScheme: Object,
  backgroundAnimation: {
    type: Object,
    default: () => PositiveWaveLoop1,
  },
})

const emit = defineEmits(['timeline', 'timelineOut'])

const barElement = ref(null)

const timelineSuperTitle = ref(null)
const timelineTitle = ref(null)
const timelineSubtitle = ref(null)
const timelineSubtitleOut = ref(null)
const timelineImage = ref(null)

onMounted(() => {
  const timeline = gsapTimeline({ name: 'Bar' })
    .from(barElement, { height: 0, duration: frames(8), ease: 'power3.in' }, '<')
    .add(timelineTitle, '<.16')
    .add(timelineSubtitle, '<.16')
    .add(timelineImage, '<.16')
    .add(timelineSuperTitle, '<.16')

  emit('timeline', timeline)

  const timelineOut = () => {
    timelineSuperTitle.value?.reverse()
    timelineTitle.value?.reverse()
    timelineImage.value?.reverse()

    return gsapTimeline({ name: 'Bar - End' })
      .add(timelineTitle)
      .add(timelineSubtitleOut.value(), '<.12')
      .add(timelineSuperTitle, '<')
      .add(props.image && timelineImage, '<.12')
      .to(barElement, { height: 0, duration: frames(8), ease: 'power3.out' }, '<.40')
  }

  emit('timelineOut', timelineOut)
})

const barBodyElement = ref(null)
const initialBody = props.body
const displayBody = ref(props.body)
watch(
  () => props.body,
  (newBody) => {
    gsapTimeline({ name: 'Bar - Body Change' })
      .fromTo(barBodyElement, { opacity: 1, y: 0 }, { opacity: 0, y: 10, duration: frames(10) })
      .add(() => (displayBody.value = newBody))
      .add(() => {
        // We need to wait a tick for the barBodyElement's height to be updated.
        nextTick(() => {
          const verticalPadding = 32
          const threeLinesHeight = 48 * 1.1 * 3 // Font size * line height * number of lines
          const contentHeight = Math.min(barBodyElement.value?.scrollHeight || 0, threeLinesHeight)
          const newHeight = contentHeight === 0 ? 0 : contentHeight + verticalPadding * 2 // Sometimes the scrollHeight is retrieved before the browser has applied the line clamp (somehow), so we restrict the height.
          gsapTimeline({ name: 'Bar - Update' }).to(barElement, { height: newHeight, duration: frames(10), ease: 'linear' })
        })
      })
      .fromTo(barBodyElement, { opacity: 0, y: 40 }, { opacity: 1, y: 0, duration: frames(10) }, `<${frames(6)}`)
  }
)

const displaySubtitle = ref(props.subtitle)
watch(
  () => props.subtitle,
  (newSubtitle) => {
    gsapTimeline({ name: 'Bar - Subtitle Change' })
      .add(timelineSubtitleOut.value())
      .add(() => (displaySubtitle.value = newSubtitle))
      .add(timelineSubtitle, `<${frames(3)}`)
  }
)

const latestImage = ref(props.image)
const showImage = (state) => {
  timelineImage.value.reversed(!state)
  return gsapTimeline({ name: `Bar - Image ${state ? 'In' : 'Out'}` }).add(timelineImage)
}
watch(
  () => props.image,
  async (newImage, oldImage) => {
    if (newImage) {
      if (oldImage) {
        await showImage(false)
      }
      latestImage.value = newImage
      showImage(true)
    } else {
      showImage(false)
    }
  }
)
</script>

<template>
  <div class="absolute bottom-0 p-safe z-10 w-full">
    <div class="flex gap-12 items-end">
      <div class="w-5/6 flex flex-col justify-end">
        <CharacterEnlarge v-if="superTitle" :text="superTitle" class="text-white text-[9rem] font-bold leading-none -mb-2" @timeline="timelineSuperTitle = $event" />
        <div class="flex items-baseline space-x-4">
          <CharacterJumpFromTheRight v-if="title" :text="title" class="text-q-cr-red-primary" :class="!subtitle && 'flex-1'" @timeline="timelineTitle = $event" />
          <TextSlideUp
            class="text-white font-qarla normal-case text-5xl line-clamp-1 leading-[1.1]"
            :text="displaySubtitle"
            @timeline="timelineSubtitle = $event"
            @timelineOut="timelineSubtitleOut = $event"
          />
        </div>
        <div ref="barElement" class="overflow-hidden text-white rounded-3xl" :class="!initialBody && 'h-0'">
          <AnimatedBackground :lottie="backgroundAnimation" :backgroundColor="colorScheme.backgroundColor" :color="colorScheme.color" :position="{ width: 1920, height: 1080 }" class="px-12 py-[32px]">
            <!-- Please note: the font size / line height is also used in the `threeLinesHeight` variable. -->
            <p ref="barBodyElement" class="font-qarla normal-case text-5xl line-clamp-3 leading-[1.1]">{{ displayBody }}</p>
          </AnimatedBackground>
        </div>
      </div>
      <AnimatedSkewedEllipsMask class="flex-1 w-full h-[15rem]" @timeline="timelineImage = $event">
        <img v-if="latestImage" :src="latestImage" class="object-cover h-full w-full" />
      </AnimatedSkewedEllipsMask>
    </div>
  </div>
</template>
